import React, { useContext, useEffect, useMemo, useState } from "react";


import { doCheckOffers } from "../../../logics/src/utils/checkOffers";
import { getOfferMessage } from "../../../logics/src/utils/helpers";
import { getHexToRgba } from "@nykaa/payment-sdk/src/common/helpers/getHexToRgba";
import { INetBankingDetails } from "@nykaa/payment-sdk/src/Payment/@types";
import { PAYMENT_MODES } from "constants/paymentModes";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { useTheme } from "emotion-theming";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { toLowerCase } from "helpers/utils/toLowerCase";
import { getStringFromId } from "helpers/vernacular";
import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { OfferDataInterface } from "wrapper/PaymentWrapper/dependencies/utils/@types/savedMethods";
import { getPlatform } from "../../../helpers";
import { DEVICE_TYPE, BUTTON_TYPE } from "constants/index";

import NetBankingDesktop from "@nykaa/payment-sdk/src/NetBanking/desktop";
import NetBankingMobile from "@nykaa/payment-sdk/src/NetBanking/mobile";
import { getBankImage } from "./helpers";
import { useHistory } from "react-router-dom";


interface IprioritizedBanksList {
  id: string;
  index: number;
  text: string;
  name: string;
  icon?: string;
  endIcon: string;
  priority?: number;
}

interface IBanksList {
  cashfreeCode: string;
  endIcon: string;
  id: string;
  index: number;
  name: string;
  offerKeys?: string[];
  payuCode: string;
  priority: number;
  razorpayCode: string;
  status: boolean;
  text: string;
}

const NetBankingWrapper = ({
  cartRules,
  doPayment,
  isAccordianOpened,
  toggleNewPaymentAccordian,
  endIcon,
  isDisabled,
  strikedPaymentText,
}: any) => {
  const NORMAL_BANK_PRIORITY = 0;
  const paymentContext: any = useContext(PaymentContext);
  const { applicationData } = useContext(ApplicationContext);
  const theme = useTheme() as Theme;
  const history = useHistory();
  const platform = getPlatform();

  const paymentInfo = paymentContext?.paymentInfo;
  const netBankingList = paymentInfo?.netBankingList;
  const downBanksList = paymentInfo?.downBanksList;
  const disabledPaymentMethodMessage = paymentInfo?.disabledPaymentMethodMessage;
  const grandTotal = paymentContext?.orderAmount;
  const isCartRulesLoading = paymentContext?.isCartRulesLoading;
  const cartInfo = paymentContext?.cartInfo;
  const initialOrderAmount = cartInfo?.grandTotal;

  const deviceType = applicationData?.deviceType;

  const [selectedBank, setSelectedBank] = useState({} as any);
  const [banksList, setBanksList] = useState({} as any);
  const [downBankMessage, setDownBankMessage] = useState("");
  const [isBottomSheetOpened, setIsBottomSheetOpened] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [offerData, setOfferData] = useState({} as any);

  useEffect(() => {
    setOfferData({});
  }, [grandTotal]);

  const getChip = (type?: string) => {
    let chipConfig = {};
    if (type === "success") {
      chipConfig = {
        text: theme?.colors?.positive,
        border: getHexToRgba(theme?.colors?.positive, 0.16),
        background: theme?.colors?.positive50,
      };
    } else {
      chipConfig = {
        text: theme?.colors?.info,
        border: getHexToRgba(theme?.colors?.info, 0.16),
        background: theme?.colors?.info50,
      };
    }
    return chipConfig;
  };

  useMemo(() => {
    let prioritizedBanksList: IprioritizedBanksList[] = [];
    let normalBanksList: IBanksList[] = [];
    /* eslint-disable no-shadow */
    let endIcon = getIcons(platform, ICONS_NAMES.RADIO);
    const downBanksListIdArr = downBanksList?.map((bank:any) => bank?.id);


    if (!netBankingList || !netBankingList.length) return;
    netBankingList.forEach((bankModel: INetBankingDetails) => {
      const isPrioritizedBank = bankModel?.priority !== NORMAL_BANK_PRIORITY;

      bankModel.disabled = false;

      if (downBanksListIdArr.includes(bankModel?.id)) {
        bankModel.disabled = true;
      }

      // logics for priority bank ui
      if (isPrioritizedBank) {
        bankModel.endIcon = bankModel.disabled ? getIcons("", ICONS_NAMES.ALERT) : endIcon;
        const length = prioritizedBanksList.length;
        bankModel.icon = getBankImage(toLowerCase(bankModel.name));
        bankModel.text = bankModel.name;
        bankModel.index = length;
        if (bankModel.offerKeys) {
          /* eslint-disable no-shadow */
          const offerData = doCheckOffers(
            false,
            PAYMENT_MODES.NET_BANKING,
            cartRules,
            paymentInfo,
            bankModel.offerKeys
          );
          if (offerData) {
            bankModel.chipText = getOfferMessage(offerData);
            bankModel.chipColor = getChip();
          }
        }
        // selecting first bank as default
        if (length === 0 && !bankModel.disabled) {
          bankModel.endIcon = getIcons(platform, ICONS_NAMES.RADIO_ACTIVE);
          setSelectedBank(bankModel);
        }
        prioritizedBanksList.push(bankModel);
      } else {
        // logics for normal banks ui
        bankModel.endIcon = bankModel.disabled ? getIcons("", ICONS_NAMES.ALERT) : endIcon;
        const length = normalBanksList.length;
        bankModel.text = bankModel.name;
        bankModel.index = length;
        if (bankModel.offerKeys) {
          /* eslint-disable no-shadow */
          const offerData = doCheckOffers(
            false,
            PAYMENT_MODES.NET_BANKING,
            cartRules,
            paymentInfo,
            bankModel.offerKeys
          );
          if (offerData) {
            bankModel.chipText = getOfferMessage(offerData);
            bankModel.chipColor = getChip();
          }
        }
        normalBanksList.push(bankModel);
      }
    });

    // logic for adding other bank in prioritized banks for ui
    const otherBankModel = {
      id: "otherbank",
      index: prioritizedBanksList.length,
      text: "Other Bank",
      name: "Other Bank",
      icon: getIcons(platform, ICONS_NAMES.OTHER_BANK),
      endIcon: getIcons(platform, ICONS_NAMES.RADIO),
      priority: prioritizedBanksList.length,
    };

    if (downBanksList?.length || normalBanksList?.length) {
      prioritizedBanksList.push(otherBankModel);
    }

    setBanksList({
      downBanksList,
      prioritizedBanksList,
      normalBanksList,
    });
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [netBankingList, downBanksList, cartRules]);

  const toggleModal = (e?: Event) => {
    setIsBottomSheetOpened(!isBottomSheetOpened);
    setIsDropdownVisible(true);
    e?.stopPropagation();
  };

  useEffect(() => {
    if (!isCartRulesLoading && selectedBank.offerKeys?.length) {
      const offer: OfferDataInterface = doCheckOffers(
        false,
        PAYMENT_MODES.NET_BANKING,
        cartRules,
        paymentInfo,
        selectedBank?.offerKeys
      );
      selectedBank.chipColor = getChip("success");
      setOfferData(offer);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isCartRulesLoading]);

  const handleBankSelect = (bank: INetBankingDetails) => {
    if (bank?.disabled) {
      return;
    }

    if (!bank) {
      const error = new Error("Bank not selected");
      throw error;
    }

    if (bank?.offerKeys?.length) {
      const offer: OfferDataInterface = doCheckOffers(
        false,
        PAYMENT_MODES.NET_BANKING,
        cartRules,
        paymentInfo,
        bank?.offerKeys
      );
      bank.chipColor = getChip("success");
      setOfferData(offer);
    } else {
      let offer = {
        hasOffers: false,
        finalOrderAmount: 0,
      };
      selectedBank.chipColor = getChip();
      setOfferData(offer);
    }

    // logics for end Icon handling when selected bank
    let otherBankList: IBanksList[] = banksList?.normalBanksList;
    let priorityBankList = banksList?.prioritizedBanksList;
    const lastSelectedBankId = selectedBank?.index as number;
    const selectedBankId = bank.index;
    // last selected bank uncheck handling
    if (lastSelectedBankId !== undefined) {
      if (selectedBank && selectedBank?.priority) {
        priorityBankList[lastSelectedBankId].endIcon = getIcons(
          platform,
          ICONS_NAMES.RADIO
        );
      } else {
        otherBankList[lastSelectedBankId].endIcon = getIcons(
          platform,
          ICONS_NAMES.RADIO
        );
      }
    }

    // new selected bank handling
    if (bank && bank?.priority) {
      // unchecking other bank only incase of priority bank select other than this
      if (selectedBankId !== priorityBankList.length - 1) {
        priorityBankList[priorityBankList.length - 1].endIcon = getIcons(
          platform,
          ICONS_NAMES.RADIO
        );
      }
      priorityBankList[selectedBankId].endIcon = getIcons(
        platform,
        ICONS_NAMES.RADIO_ACTIVE
      );
    } else {
      otherBankList[selectedBankId].endIcon = getIcons(
        platform,
        ICONS_NAMES.RADIO_ACTIVE
      );
    }

    // logics for dropdown to be visible incase of other bank
    if (selectedBankId === priorityBankList.length - 1 && bank?.priority > 0) {
      toggleModal();
    } else if (isDropdownVisible) {
      setIsDropdownVisible(false);
    }

    // selected bank must be first bank of normal banks when other banks is selected
    if (bank && bank?.id === "otherbank") {
      let normalBank: INetBankingDetails = banksList?.normalBanksList[0];
      if (!normalBank?.disabled) {
        normalBank.endIcon = getIcons(platform, ICONS_NAMES.RADIO_ACTIVE);
        setSelectedBank(normalBank);
      } else {
        /**
         * clearing previous selected bank if 1 bank is disabled;
         */
        setSelectedBank({});
      }
      toggleModal();
    } else {
      setSelectedBank(bank);
    }

    setBanksList({
      downBanksList: banksList.downBanksList,
      prioritizedBanksList: priorityBankList,
      normalBanksList: otherBankList,
    });
  };

  // Logic for showing down bank names
  // Examples: "abc bank" is down .......
  // Examples: "abc bank" & "Pqr bank" are down .......
  // Examples: "abc bank", "xyz bank" & "Pqr bank" are down .......
  useMemo(() => {
    const { downBanksList } = banksList;
    if (!downBanksList || !downBanksList.length) {
      return null;
    }
    let bankNamesString = "";
    const downBanksArrayLength = downBanksList && downBanksList.length;

    if (downBanksArrayLength > 0) {
      bankNamesString = downBanksList[0]?.name;
      for (let i = 1; i < downBanksArrayLength - 1; i++) {
        bankNamesString += ", " + downBanksList[i]?.name;
      }
      if (downBanksArrayLength > 1) {
        bankNamesString +=
          " & " + downBanksList[downBanksArrayLength - 1]?.name;
      }
    }
   /**
    * Using Api driven message instead of hard coded message
    */
    bankNamesString = disabledPaymentMethodMessage?.replace("<value>", bankNamesString);
    setDownBankMessage(bankNamesString);
    return null;
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [banksList]);


  let title = getStringFromId(
    VERNACULAR_STRING_IDS.NETBANKING_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.NETBANKING_TITLE
  );
  if (deviceType === DEVICE_TYPE.DESKTOP) {
    title = getStringFromId(
      VERNACULAR_STRING_IDS.NETBANKING_TITLE_DESKTOP,
      VERNACULAR_DEFAULT_MESSAGES.NETBANKING_TITLE_DESKTOP
    );
  }
  const subTitle = getStringFromId(
    VERNACULAR_STRING_IDS.NETBANKING_SUBTITLE,
    VERNACULAR_DEFAULT_MESSAGES.NETBANKING_SUBTITLE
  );
  const otherBank = getStringFromId(
    VERNACULAR_STRING_IDS.OTHER_BANK,
    VERNACULAR_DEFAULT_MESSAGES.OTHER_BANK
  );
  const selectYourBank = getStringFromId(
    VERNACULAR_STRING_IDS.SELECT_YOUR_BANK,
    VERNACULAR_DEFAULT_MESSAGES.SELECT_YOUR_BANK
  );
  let payNowWithStrikedAmountString = strikedPaymentText;
  if (offerData?.hasOffers) {
    payNowWithStrikedAmountString = getStringFromId(
      VERNACULAR_STRING_IDS.DYNAMIC_PAY_WITH_STRIKED_AMOUNT,
      `Pay ₹${offerData?.finalOrderAmount} &nbsp; <del> ₹${initialOrderAmount}</del>`,
      {
        oldAmount: initialOrderAmount,
        newAmount: offerData?.finalOrderAmount || "",
      }
    );
  }

  const payNowString = getStringFromId(
    VERNACULAR_STRING_IDS.DYNAMIC_PAY,
    `Pay ₹${grandTotal}`,
    { count: grandTotal }
  );

  const config = {
    title,
    subTitle,
    endIcon,
    leadingIcon: getIcons(platform, ICONS_NAMES.NETBANKING),
    doPayment: () => {
      if (!Object.keys(selectedBank).length || !selectedBank?.id) {
        // openErrorModal(PAYMENT_ERROR_MESSAGE.SOMETHING_WENT_WRONG, "Please Select the Bank");
        return;
      }

      if (isBottomSheetOpened) {
        toggleModal();
      }
      let params: any = {
        selectedBank,
        // Required for adding Money to wallet and giftcard purchase
        grandTotal,
      };
      if (offerData?.hasOffers) {
        params.offerData = offerData;
      }
      doPayment(PAYMENT_MODES.NET_BANKING, params);
    },
    chooseBank: handleBankSelect,
    otherBanks: otherBank,
    banks: banksList,
    selectedBankName: selectedBank?.name,
    toggleModal,
    bottomSheetTitle: selectYourBank,
    payNowText: payNowString,
    revisedPayNowText: payNowWithStrikedAmountString,
    isDropdownVisible,
    dropdownIcon: getIcons(platform, ICONS_NAMES.DROPDOWN_DOWN),
    downBankMessage,
    sideBarVisible: false,
    isBottomSheetOpened: false,
    closeIcon: "",
    isAccordianOpened,
    toggleAccordian: () => toggleNewPaymentAccordian(PAYMENT_MODES.NET_BANKING, isDisabled),
    isDisabled,
    deviceType,
    buttonType: BUTTON_TYPE.MEDIUM,
    theme,
    history,
  };

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    config.sideBarVisible = isBottomSheetOpened;
  } else {
    config.isBottomSheetOpened = isBottomSheetOpened;
    config.closeIcon = getIcons(platform, ICONS_NAMES.CLEAR);
  }

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    return <NetBankingDesktop {...config} />;
  }

  return <NetBankingMobile {...config} theme={theme} />;
};

export default NetBankingWrapper;
